import {
    ContextualMenuItemType,
    DirectionalHint,
    getTheme,
    ImageCoverStyle,
    ImageFit,
    Link,
    mergeStyleSets,
    Stack,
    TeachingBubble,
} from '@fluentui/react'
import { useBoolean } from '@fluentui/react-hooks'
import { HeadsetRegular } from '@fluentui/react-icons'
import * as React from 'react'
import { FunctionComponent, useContext, useMemo } from 'react'
import IconValue from '../IconValue/IconValue'
import { mergeStyles } from '@fluentui/react/lib/Styling'
import DiagnosticsDialog from './DiagnosticsDialog'
import { getApplicationIconPropsByType } from '../../helpers/iconHelper'
import { DocumentType } from '../../helpers/documentHelper'
import { getId, IStyleFunctionOrObject } from '@fluentui/utilities'
import { ILinkStyleProps, ILinkStyles } from '@fluentui/react/src/components/Link/Link.types'
import { AppAction } from '../../config/appConfig'
import useLogOut from '../../hooks/lifecycle/useLogOut'
import useModules from '../../hooks/module/useModules'
import { ModuleType, ThemeOption } from '../../context/GlobalContext'
import { useTranslation } from 'react-i18next'
import I18NContext from '../../context/I18NContext'
import { Button } from '@fluentui/react-components'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { useApplicationStyles } from '../../pages/App'
import AuthorizationContext from '../../context/AuthorizationContext'
import useLocalStorage from '../../hooks/useLocalStorage'

type BubbleProps = { toggleBubble: () => void; toggleDiagnostics: () => void }

const theme = getTheme()
const classNames = mergeStyleSets({
    lightText: mergeStyles({ color: theme.palette.themeLighterAlt }),
})

const SupportStyles: IStyleFunctionOrObject<ILinkStyleProps, ILinkStyles> = {
    root: [
        {
            color: theme.palette.themeLighterAlt,
            marginLeft: '0 !important',
            selectors: {
                ':hover': {
                    color: theme.palette.themeLighterAlt,
                    fontWeight: 'bold',
                },
                ':focus': {
                    color: theme.palette.themeLighterAlt,
                },
                ':active': {
                    color: theme.palette.themeLighterAlt,
                },
            },
        },
    ],
}

const Bubble: FunctionComponent<BubbleProps> = props => {
    const { t: translate } = useTranslation()
    const { setLanguage } = useContext(I18NContext)

    const logOut = useLogOut()
    const { authenticated } = useContext(AuthorizationContext)
    const modules = useModules()

    const { setValues: setCurrentThemeOption } = useLocalStorage<ThemeOption>({
        key: 'currentTheme',
        defaultValue: ThemeOption.Auto,
        fromJson: values => values,
        toJson: values => values,
    })

    const menuItems = [
        {
            key: 'user-manual',
            iconProps: { iconName: 'ReadingMode' },
            text: translate('support.toolbox.manual'),
            onClick: () => {
                window.open(process.env.USER_MANUAL_URL, '_blank')
            },
        },
        {
            key: 'anydesk',
            iconProps: {
                imageProps: {
                    src: `./images/anydesk.png`,
                    height: 16,
                    width: 16,
                    imageFit: ImageFit.contain,
                    coverStyle: ImageCoverStyle.landscape,
                },
            },
            text: translate('support.toolbox.anydesk'),
            onClick: () => {
                window.open(process.env.ANY_DESK_URL, '_blank')
            },
        },
        {
            key: 'teamviewer',
            iconProps: getApplicationIconPropsByType(DocumentType.TeamViewer, 16),
            text: translate('support.toolbox.teamviewer'),
            onClick: () => {
                window.open(process.env.TEAM_VIEWER_URL, '_blank')
            },
        },
        {
            key: 'authorisations',
            iconProps: { iconName: 'LinkedDatabase' },
            text: translate('support.toolbox.autorizations.label'),
            disabled: !authenticated,
            items: [
                {
                    key: getId(),
                    itemType: ContextualMenuItemType.Header,
                    text: translate('support.toolbox.autorizations.accounting'),
                },
                {
                    key: 'authorise-exact',
                    iconProps: getApplicationIconPropsByType(DocumentType.Exact, 16),
                    text: 'Exact Online',
                    onClick: () => {
                        window.open(process.env.EXACT_AUTH_URL, '_blank')
                    },
                    disabled: !modules.has(ModuleType.Accounting),
                },
                {
                    key: 'authorise-twinfield',
                    iconProps: getApplicationIconPropsByType(DocumentType.Twinfield, 16),
                    text: 'Twinfield',
                    disabled: true,
                },
                {
                    key: 'authorise-mollie',
                    iconProps: getApplicationIconPropsByType(DocumentType.Mollie, 16),
                    text: 'Mollie',
                    onClick: () => {
                        window.open(process.env.MOLLIE_AUTH_URL, '_self')
                    },
                    disabled: !modules.has(ModuleType.Mollie),
                },
                { key: getId(), itemType: ContextualMenuItemType.Header, text: 'Microsoft' },
                {
                    key: 'authorise-exchange',
                    iconProps: { iconName: 'ExchangeLogo' },
                    text: 'Microsoft Exchange',
                    disabled: true,
                },
                {
                    key: 'authorise-sharepoint',
                    iconProps: { iconName: 'SharepointLogo' },
                    text: 'Microsoft SharePoint',
                    disabled: true,
                },
            ],
        },
        {
            key: 'languages',
            iconProps: { iconName: 'LocaleLanguage' },
            text: translate('support.toolbox.languages'),
            items: [
                {
                    key: getId(),
                    itemType: ContextualMenuItemType.Header,
                    text: translate('support.toolbox.languages'),
                },
                {
                    key: 'nl',
                    iconProps: {
                        imageProps: {
                            src: `https://flagcdn.com/16x12/nl.png`,
                            height: 16,
                            width: 16,
                            imageFit: ImageFit.contain,
                            coverStyle: ImageCoverStyle.landscape,
                        },
                    },
                    text: 'Nederlands',
                    onClick: () => {
                        setLanguage('nl')
                    },
                },
                {
                    key: 'en',
                    iconProps: {
                        imageProps: {
                            src: `https://flagcdn.com/16x12/gb.png`,
                            height: 16,
                            width: 16,
                            imageFit: ImageFit.contain,
                            coverStyle: ImageCoverStyle.landscape,
                        },
                    },
                    text: 'English',
                    onClick: () => {
                        setLanguage('en')
                    },
                },
                ['test', 'dev'].includes(process.env.BOOTSTRAP_ENV) && {
                    key: 'cimode',
                    iconProps: {
                        imageProps: {
                            src: `./icons/cimode.png`,
                            height: 16,
                            width: 16,
                            imageFit: ImageFit.contain,
                            coverStyle: ImageCoverStyle.landscape,
                        },
                    },
                    text: 'Interactive mode',
                    onClick: () => {
                        setLanguage('cimode')
                    },
                },
            ],
        },
        {
            key: 'theme',
            iconProps: { iconName: 'SunQuestionMark' },
            text: translate('support.toolbox.themes'),
            items: [
                {
                    key: getId(),
                    itemType: ContextualMenuItemType.Header,
                    text: translate('support.toolbox.themes'),
                },
                {
                    key: 'light',
                    text: translate('support.toolbox.themes.light'),
                    iconProps: { iconName: 'Sunny' },
                    onClick: () => {
                        setCurrentThemeOption(ThemeOption.Light)
                    },
                },
                {
                    key: 'dark',
                    text: translate('support.toolbox.themes.dark'),
                    iconProps: { iconName: 'ClearNight' },
                    onClick: () => {
                        setCurrentThemeOption(ThemeOption.Dark)
                    },
                },
                {
                    key: 'auto',
                    text: translate('support.toolbox.themes.auto'),
                    iconProps: { iconName: 'SunQuestionMark' },
                    onClick: () => {
                        setCurrentThemeOption(ThemeOption.Auto)
                    },
                },
            ],
        },
        {
            key: 'diagnostiek',
            iconProps: { iconName: 'Diagnostic' },
            text: translate('support.toolbox.diagnostics.label'),
            onClick: () => {
                props.toggleDiagnostics()
                props.toggleBubble()
            },
        },
        {
            key: 'logout',
            iconProps: { iconName: 'Refresh' },
            text: translate('support.toolbox.restart'),
            onClick: () => logOut(AppAction.Reset),
        },
    ].filter(Boolean)

    return (
        <TeachingBubble
            target="#support"
            calloutProps={{
                directionalHint: DirectionalHint.topRightEdge,
            }}
            primaryButtonProps={{
                text: translate('support.toolbox.label'),
                menuProps: {
                    items: menuItems,
                },
                iconProps: { iconName: 'Toolbox' },
            }}
            secondaryButtonProps={{
                text: translate('action.close'),
                iconProps: { iconName: 'Completed', className: classNames.lightText },
                onClick: props.toggleBubble,
            }}
            headline={translate('support.contact.label')}
        >
            <Stack tokens={{ childrenGap: 's1' }}>
                <IconValue textVariant="medium" iconName="Phone" wrap wrapperClassName={classNames.lightText}>
                    <Link href="tel:0262062199" styles={SupportStyles}>
                        026 - 206 21 99
                    </Link>
                </IconValue>
                <IconValue textVariant="medium" iconName="Mail" wrap wrapperClassName={classNames.lightText}>
                    <Link href="mailto:support@hammock.nl" styles={SupportStyles}>
                        support@hammock.nl
                    </Link>
                </IconValue>
                <IconValue textVariant="medium" iconName="Calendar" wrap wrapperClassName={classNames.lightText}>
                    {translate('support.contact.period')}: 8:30 - 17:30
                </IconValue>
            </Stack>
        </TeachingBubble>
    )
}

const Support: FunctionComponent = () => {
    const { t: translate } = useTranslation()
    const styles = useApplicationStyles()
    const { viewport, isViewport } = useViewport()

    const { authenticated } = useContext(AuthorizationContext)

    const smallMenu = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    const [showBubble, { toggle: toggleBubble }] = useBoolean(false)
    const [showDiagnostics, { toggle: toggleDiagnostics }] = useBoolean(false)

    return (
        <>
            <Button
                id={'support'}
                icon={<HeadsetRegular />}
                appearance={'subtle'}
                iconPosition={'after'}
                onClick={toggleBubble}
                className={styles.thinTextButton}
            >
                {!smallMenu && translate('support.label')}
            </Button>
            {showBubble && <Bubble toggleBubble={toggleBubble} toggleDiagnostics={toggleDiagnostics} />}
            {authenticated && showDiagnostics && <DiagnosticsDialog toggle={toggleDiagnostics} />}
        </>
    )
}

export default Support
